import { PricingError, type Basis, type GridResponse, type Market, type PriceRunLine, type PriceRuns, type ProposalCheck, type ProposeAnswer, type ProposeBody, type ProposeChannel, type ProposeEnding, type ProposeRow, type TickedRow } from "./api";
import { COPY, messageFor } from "./format";

/**
 * The strategy price run on the Price Run tab (c1-run-propose, UX v0.32 §9.10, mockup v28 frames 1 and 2). C2 works out every
 * price (c2-run-propose); this file only words C2's answer. Nothing here computes a price or judges a rule (INV-12, H12).
 */

const TIME = new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone: "UTC" });
/** A decimal string and its currency as §9.10 writes money in a reason: `20.00 GBP`. */
const amount = (value: string | null | undefined, currency: string | null | undefined) => (value && currency ? `${value} ${currency}` : null);

/** §9.10 microcopy slots, verbatim where §9.10 or mockup v28 sets them. DEC-1403 (P9-COPY) D1 to D4 fill the rest. */
export const PROPOSE_COPY = {
  button: "Propose prices from strategies",
  title: (market: Market) => `Propose prices from strategies · ${market}`,
  preview: (proposed: number, skipped: number) => `Preview: ${proposed} rows would be proposed, ${skipped} skipped. Nothing is saved yet.`,
  counts: { proposed: "Proposed", skipped: "Skipped", unverified: "Rules unverified" },
  skippedHead: "Skipped, by reason",
  rowsHead: "Rows",
  columns: ["Product · Marketplace", "Current", "Proposed"] as const,
  more: (n: number) => `+ ${n} more`,
  unverified: (label: string, rows: number, reason: string | null) => `${label} could not be checked on ${rows} ${rows === 1 ? "row" : "rows"}${reason ? `: ${reason}` : ""}.`,
  replace: (n: number) => `Replace lines already saved by a person (${n} ${n === 1 ? "line" : "lines"})`,
  save: (n: number) => `Propose · save ${n} ${n === 1 ? "line" : "lines"}`,
  saveTicked: (n: number) => `Propose ${n} ticked ${n === 1 ? "row" : "rows"}`,
  cancel: "Cancel",
  close: "Close",
  nothingToSave: "No row would be proposed, so there is nothing to save.",
  // DEC-1403 D4: the wait (EU took 262 s through the tunnel on 1 Nov).
  checking: "Checking every row against its strategy. EU can take up to 5 minutes; keep this open.",
  saving: "Saving the proposed lines. EU can take up to 5 minutes; keep this open.",
  elapsed: (s: number) => `${s} s so far`,
  done: (n: number) => `${n} ${n === 1 ? "line" : "lines"} saved, not live. Adjust what you disagree with, then Commit.`,
  error: "Nothing was saved. The store did not answer; try again.",
  retry: "Try again",
  ticked: (n: number) => `${n} ticked`,
  clearTicks: "Clear ticks",
  tickRow: (product: string, place: string) => `Tick ${product} · ${place}`,
  tickAll: "Tick every row on this page",
  channel: "Channel",
  skippedLine: (skipped: number, rows: number) => `Skipped ${skipped} of ${rows} rows`,
  showSkipped: "Show skipped",
  hideSkipped: "Hide skipped",
  strategyWas: (price: string) => `strategy ${price}`,
  badge: { met: "met", unverified: (n: number) => `${n} unverified`, notMet: (n: number) => `${n} not met` },
  proposedBy: (price: string) => `Proposed by the strategy ${price}`,
  goalPrice: (price: string) => `goal price ${price}`,
  window: (low: string, high: string | null) => (high === null ? `window ${low} and above` : `window ${low} to ${high}`),
  ending: (direction: "up" | "down", ending: number, where: string) => `rounded ${direction} to .${String(ending).padStart(2, "0")} (${where})`,
  unverifiedNamed: (labels: string[]) => `Could not be checked: ${labels.join(", ")}`,
  notMetNamed: (rules: string[]) => `Not met at the proposed price: ${rules.join(", ")}`,
  proposalHead: "Proposal",
  reviewColumn: "Proposed",
  reviewMoved: "moved off the proposal",
} as const;

/** Each skip reason as a group label (§9.10 and mockup v28; DEC-1403 D1 for the RRP pair). A reason C2 adds later reads as its words. */
export const SKIP_LABEL: Record<string, string> = {
  no_strategy: "No strategy assigned",
  no_price: "No price in force",
  no_suggestion: "No workable price (margins missing)",
  below_floor: "Below floor",
  below_rrp: "Below RRP",
  rrp_not_comparable: "RRP not comparable (no exchange rate)",
  already_saved: "Already saved",
  unchanged_price: "Unchanged price",
  not_readable: "Not readable",
};
/** The order groups are listed in: what Damien decides by hand first (below RRP, CL-028), then what he fixes elsewhere. */
const SKIP_ORDER = ["below_rrp", "rrp_not_comparable", "below_floor", "already_saved", "unchanged_price", "no_strategy", "no_price", "no_suggestion", "not_readable", "not_strategised"];

export function skipGroupLabel(reason: string, market: Market): string {
  if (reason === "not_strategised") return `Not strategised (${market})`;
  return SKIP_LABEL[reason] ?? reason.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
}

/** One skipped row's reason with its fact: the floor or RRP it breaks, or when the line already there was saved (DEC-1403 D2). */
export function skipRowLabel(row: ProposeRow, market: Market, saved: PriceRunLine | null): string {
  const skip = row.skip;
  if (!skip) return "";
  const d = skip.detail ?? {};
  const proposed = row.proposed ? ` · proposed ${amount(row.proposed.value, row.proposed.currency)}` : "";
  if (skip.reason === "below_floor") {
    const floor = amount(d.floor, d.floor_currency);
    return `${floor ? `Below floor ${floor}` : SKIP_LABEL.below_floor}${proposed}`;
  }
  if (skip.reason === "below_rrp") {
    const rrp = amount(d.rrp, d.rrp_currency);
    return `${rrp ? `Below RRP ${rrp}` : SKIP_LABEL.below_rrp}${proposed}`;
  }
  if (skip.reason === "already_saved") {
    // DEC-1403 P9-COPY-2: the time only; no person is named.
    return saved ? `Already saved at ${TIME.format(new Date(saved.saved_at))}` : SKIP_LABEL.already_saved;
  }
  if (skip.reason in SKIP_LABEL || skip.reason === "not_strategised") return skipGroupLabel(skip.reason, market);
  return skip.message;
}

export type SkipItem = { key: string; name: string; label: string; message: string };
export type SkipGroup = { reason: string; label: string; count: number; items: SkipItem[] };
export const proposeRowKey = (row: Pick<ProposeRow, "xero_sku" | "marketplace_id">) => `${row.xero_sku}|${row.marketplace_id}`;
export type MarketplaceNames = ReadonlyMap<string, string>;
/** Names served on the same grid rows the Price Run displays; proposal answers carry ids only. */
export function marketplaceNames(grids: GridResponse[]): MarketplaceNames {
  return new Map(grids.flatMap((grid) => grid.grid.rows.flatMap((row) => row.marketplace_name ? [[proposeRowKey(row), row.marketplace_name] as const] : [])));
}
export const placeName = (row: Pick<ProposeRow, "xero_sku" | "channel" | "marketplace_id">, names?: MarketplaceNames) =>
  row.channel === "shopify" ? "Shopify" : names?.get(proposeRowKey(row)) ?? row.marketplace_id;

/** The skipped rows grouped by reason, each group counted as C2 counted it (`skipped_by_reason`), in SKIP_ORDER. */
export function skippedGroups(answer: ProposeAnswer, runs: PriceRuns | undefined, names?: MarketplaceNames): SkipGroup[] {
  const lines = (runs?.runs ?? []).flatMap((run) => run.lines.filter((l) => l.state === "saved"));
  // `/price-run` serves every market's runs and a Shopify row's marketplace id is "ALL", so the market is part of the match.
  const savedOf = (row: ProposeRow) => lines.find((l) => l.market === answer.market && l.xero_sku === row.xero_sku && l.marketplace_id === row.marketplace_id) ?? null;
  const by = new Map<string, SkipItem[]>();
  for (const row of answer.rows) {
    if (row.status !== "skipped" || !row.skip) continue;
    const items = by.get(row.skip.reason) ?? [];
    items.push({ key: proposeRowKey(row), name: `${row.product} ${row.variant} · ${placeName(row, names)}`, label: skipRowLabel(row, answer.market, savedOf(row)), message: row.skip.message });
    by.set(row.skip.reason, items);
  }
  const rank = (reason: string) => { const i = SKIP_ORDER.indexOf(reason); return i < 0 ? SKIP_ORDER.length : i; };
  return [...by.entries()].sort(([a], [b]) => rank(a) - rank(b) || a.localeCompare(b))
    .map(([reason, items]) => ({ reason, label: skipGroupLabel(reason, answer.market), count: answer.skipped_by_reason[reason] ?? items.length, items }));
}

/** One line per metric C2 could not check (`unverified_by_rule`), named by the rule's label and why (R10, DEC-1368 A). */
export function unverifiedLines(answer: ProposeAnswer): string[] {
  return Object.entries(answer.unverified_by_rule).sort(([a], [b]) => a.localeCompare(b)).map(([metric, rows]) => {
    const rule = answer.rows.flatMap((r) => r.unverified).find((u) => u.metric === metric);
    return PROPOSE_COPY.unverified(rule?.label ?? metric, rows, rule?.reason ?? null);
  });
}
/** Rows a real pass would replace when the box is ticked: the dry run's `already_saved` count. */
export const replaceCount = (answer: ProposeAnswer | null) => answer?.skipped_by_reason.already_saved ?? 0;

/** The channel the run is scoped to: the grid's Channel chip (`f_channel`, the label `channelLabel` writes). */
export function proposeChannel(filter: string | null): ProposeChannel {
  return filter === "Amazon" ? "amazon" : filter === "Shopify" ? "shopify" : "all";
}

/** The body of one pass (R9). The ticked rows go only when there are some; a pass with none runs the whole channel. */
export function proposeBody(market: Market, basis: Basis, channel: ProposeChannel, ticked: TickedRow[], dryRun: boolean, replaceSaved: boolean): ProposeBody {
  return { market, basis, dry_run: dryRun, replace_saved: replaceSaved, channel, ...(ticked.length > 0 ? { skus: ticked } : {}) };
}

/** Whether a region offers the button (DEC-1403 D3, §9.10 item 4): one market, and never AE until a strategy exists there. */
export function proposeMarket(markets: Market[], strategised: Partial<Record<Market, boolean>>): Market | null {
  if (markets.length !== 1) return null;
  const market = markets[0];
  if (market === "AE" && strategised.AE !== true) return null;
  return market;
}

/** The sheet's states (§9.10 ProposeSheet): reading the preview, the preview, saving, done, refused. */
export type SheetState =
  | { phase: "checking" } | { phase: "preview"; answer: ProposeAnswer } | { phase: "saving"; answer: ProposeAnswer }
  | { phase: "done"; answer: ProposeAnswer } | { phase: "error"; text: string; answer: ProposeAnswer | null };
/** While a pass is in flight every action is off: no double submit, no Cancel mid-write (§9.10, master requirement c). */
export const sheetBusy = (s: SheetState) => s.phase === "checking" || s.phase === "saving";

/** A refused pass in words: 503 nothing saved (§9.10), the ticked rows C2 does not hold, else C2's message or the access line. */
export function proposeRefusal(error: unknown, areaName: string): string {
  if (error instanceof PricingError) {
    const facts = error.facts() as { message?: string; rows?: unknown };
    if (error.status === 503) return PROPOSE_COPY.error;
    if (error.status === 422 && error.reason() === "unknown_rows" && Array.isArray(facts.rows)) {
      const names = (facts.rows as Array<string | { xero_sku?: string; marketplace_id?: string }>).map((r) => (typeof r === "string" ? r : `${r.xero_sku} on ${r.marketplace_id}`));
      return `${facts.message ?? "these ticked rows are not rows of the market"}: ${names.join(", ")}. Nothing was saved.`;
    }
    if ((error.status === 422 || error.status === 404) && facts.message) return `${facts.message}. Nothing was saved.`;
    if (error.status === 401 || error.status === 403) return messageFor(error, areaName);
  }
  if (!(error instanceof PricingError)) return PROPOSE_COPY.error;
  return COPY.unreachable;
}

/** The badge (§9.10, DEC-1403 P9-COPY-2 behaviour 5, master calls in the spec): text, never colour alone, from the rule check C2 saved or
 * returned. With no rule checked it never says `met`. No check, no badge: none is guessed. */
export function badgeText(check: Partial<ProposalCheck> | null | undefined): string | null {
  if (!check?.checked) return null;
  const unverified = check.unverified?.length ? PROPOSE_COPY.badge.unverified(check.unverified.length) : null;
  if (check.checked.length === 0) return unverified;
  const notMet = check.not_met?.length ?? 0;
  return [notMet > 0 ? PROPOSE_COPY.badge.notMet(notMet) : PROPOSE_COPY.badge.met, unverified].filter(Boolean).join(" · ");
}
/** The same badge from a pass's own row, as the preview table shows it. */
export const rowBadge = (row: ProposeRow): string | null => badgeText(row.check);

/** The rounding step in words (R11, DEC-1367): applied names direction, ending and whose; not applied is C2's own reason. */
export function endingLine(ending: ProposeEnding | null | undefined, market: Market): string | null {
  if (!ending) return null;
  if (ending.applied && ending.direction && ending.ending !== null) {
    return PROPOSE_COPY.ending(ending.direction, ending.ending, ending.source === "strategy" ? "strategy" : `${market} Config`);
  }
  return ending.reason;
}

/** Whether a saved line has moved off its proposal: C2's two strings compared as written, never as numbers. */
export const movedOff = (line: Pick<PriceRunLine, "new_price" | "proposal">) => Boolean(line.proposal && line.proposal.price !== line.new_price);
